import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Users, FileSpreadsheet, Edit } from 'lucide-react';
import CalendarTable from '../page_components/CalendarTable';
import MobileAgenda from '../page_components/MobileAgenda';
import MonthNavigation from '../page_components/MonthNavigation';
import StatusModal from '../modals/StatusModal';
import VacationWarningModal from '../modals/VacationWarningModal';
import GenerateReportModal from '../modals/GenerateReportModal';
import Modal from '../modals/Modal';
import Header from '../page_components/Header';
import StatsCards from '../page_components/StatsCards';
import PasswordChangeModal from '../modals/PasswordChangeModal';
import DeleteConfirmModal from '../modals/DeleteConfirmModal';
import CollectiveVacationForm from '../forms/CollectiveVacationForm';
import useCalendarData from '../../hooks/useCalendarData';
import { useYearStatus } from '../../contexts/YearStatusContext';
import useSelection from '../../hooks/useSelection';
import useIsMobile from '../../hooks/useIsMobile';
import { API_ENDPOINTS, addQueryParams, API_PATTERNS } from '../../config/api';

function Dashboard({
  currentUser,
  vacationBalance,
  onLogout,
  currentUserRole,
  currentView,
  setCurrentView,
  currentLanguage,
  setCurrentLanguage,
  t,
  fetchVacationBalance,
  fetchVacationBalanceForYear,
  vacationBalanceForYear,
  showChangePassword,
}) {
  // State for UI
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const currentMonthRef = useRef(new Date()); // Internal ref for operations
  const [vacationWarningModal, setVacationWarningModal] = useState({
    isOpen: false,
    adminMode: false,
    remaining: 0,
    requested: 0
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCollectiveVacationForm, setShowCollectiveVacationForm] = useState(false);
  const [collectiveVacationData, setCollectiveVacationData] = useState({ date: '', description: '' });
  const [collectiveVacations, setCollectiveVacations] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showGenerateReportModal, setShowGenerateReportModal] = useState(false);
  const [vacationToDelete, setVacationToDelete] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [adminEditMode, setAdminEditMode] = useState(false);
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: ''
  });

  const translateWithFallback = (key, fallback, params = {}) => {
    if (typeof t !== 'function') {
      return fallback;
    }
    const value = t(key, params, currentLanguage);
    if (!value || value === key) {
      return fallback;
    }
    return value;
  };

  // Custom hooks
  const { yearStatus, refetchYearStatus } = useYearStatus();

  // Format current user name for display (F. Surname)
  const formatDisplayName = (firstName, lastName) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}. ${lastName}`;
    }
    return firstName || lastName || '';
  };

  const currentUserDisplay = currentUser ? formatDisplayName(currentUser.first_name, currentUser.last_name) : null;

  // Fetch collective vacations
  const fetchCollectiveVacations = useCallback(async () => {
    try {

      // Get current year date range to fetch collective vacations
      const currentYear = yearStatus?.currentOpenYear || new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;

      const response = await fetch(addQueryParams(API_ENDPOINTS.COLLECTIVE_VACATION, {
        start_date: startDate,
        end_date: endDate
      }), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCollectiveVacations(data.collective_vacations || []);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch collective vacations:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error fetching collective vacations:', error);
    }
  }, [yearStatus?.currentOpenYear]);

  // Delete collective vacation
  const deleteCollectiveVacation = async (id) => {
    try {
      const response = await fetch(API_PATTERNS.DELETE_COLLECTIVE_VACATION(id), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchCollectiveVacations(); // Refresh list
        loadWorkStatusesForMonth(currentMonthRef.current); // Use ref instead of state
        // No success alert - just refresh the data
      } else {
        const error = await response.json();
        alert(t('error', { error: error.error }, currentLanguage));
      }
    } catch (error) {
      console.error('Error deleting collective vacation:', error);
      alert(t('errorDeletingCollectiveVacation', {}, currentLanguage));
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (vacationToDelete) {
      deleteCollectiveVacation(vacationToDelete.id);
      setShowDeleteConfirmModal(false);
      setVacationToDelete(null);
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = (vacation) => {
    setVacationToDelete(vacation);
    setShowDeleteConfirmModal(true);
  };

  const handleErrorModalClose = useCallback(() => {
    setErrorModal({ isOpen: false, message: '' });
  }, []);

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert(t('passwordMismatch', {}, currentLanguage));
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.CHANGE_PASSWORD, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      if (response.ok) {
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        // No success alert - just close modal
      } else {
        let errorMessage = 'Unknown error';
        try {
          const error = await response.json();
          errorMessage = error.error || 'Unknown error';
        } catch (parseError) {
          // If JSON parsing fails, use response status text
          errorMessage = response.statusText || `HTTP ${response.status}`;
        }

        if (response.status === 401) {
          alert(t('unauthorizedAccess', {}, currentLanguage));
        } else if (errorMessage === 'Current password is incorrect') {
          alert(t('incorrectPassword', {}, currentLanguage));
        } else {
          alert(t('error', { error: errorMessage }, currentLanguage));
        }
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert(t('errorChangingPassword', {}, currentLanguage));
    }
  };

  // Custom hooks
  const {
    teamMembers,
    loading,
    error,
    getStatusForUserAndDate,
    updateStatus,
    getTeamStats,
    loadWorkStatusesForMonth,
    loadHolidays,
    isCroatianHoliday,
    getHolidayName,
    userEmploymentData,
    getFullNameFromDisplay
  } = useCalendarData(currentUserDisplay, (errorMessage) => {
    setErrorModal({
      isOpen: true,
      message: errorMessage
    });
  }, currentMonthRef, vacationBalance); // Pass ref instead of state

  const {
    selectedDay,
    selectedRange,
    selectedUser,
    clearSelection,
    handleCellClick,
    handleMouseDown,
    handleMouseEnter,
    handleMouseUp,
    isDateSelected,
  } = useSelection(isCroatianHoliday, currentUserRole, adminEditMode, collectiveVacations);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (!error) {
      return;
    }
    setErrorModal(prev => {
      if (prev.isOpen && prev.message === error) {
        return prev;
      }
      return {
        isOpen: true,
        message: error
      };
    });
  }, [error]);

  // Handle drag completion - show status modal
  const handleDragComplete = () => {
    setShowStatusModal(true);
  };

  // Create wrapped handleMouseUp that includes drag completion callback
  const wrappedHandleMouseUp = () => {
    handleMouseUp(handleDragComplete);
  };

  // Wrapper function to check for closed years before allowing cell clicks
  const handleCellClickWithValidation = (date, user, currentUser, event) => {
    // Check if the year is closed
    if (yearStatus && yearStatus.closedYears) {
      const clickedYear = date.getFullYear();
      if (yearStatus.closedYears.includes(clickedYear)) {
        // Show error modal for closed year
        setErrorModal({
          isOpen: true,
          message: 'Cannot edit work status for closed year'
        });
        return; // Don't proceed with the original click handler
      }
    }

    // If year is not closed, proceed with normal click handling
    handleCellClick(date, user, currentUser, event);
  };

  const stats = getTeamStats();

  // Enhanced updateStatus function that refreshes vacation balance after vacation changes
  const updateStatusWithRefresh = async (dates, status, user, t, extraOptions = {}) => {
    // Vacation warning modal callback
    const onVacationWarning = (remaining, requested, adminMode) => {
      setVacationWarningModal({
        isOpen: true,
        adminMode,
        remaining,
        requested,
      });
    };

    // Determine if this is an admin edit and who the target user is
    const isAdminEdit = adminEditMode && currentUserRole === 'admin' && selectedUser !== currentUserDisplay;
    const targetUser = isAdminEdit ? selectedUser : null;

    // Call updateStatus with admin edit mode parameters
    const success = await updateStatus(dates, status, user, t, {
      ...extraOptions,
      onVacationWarning,
      targetUser,
      isAdminEdit,
    });

    // If the operation was successful and involved vacation status, refresh vacation balance
    if (success && (status === 'vacation' || status === null)) {
      // Refresh vacation balance for real-time updates
      fetchVacationBalance();
      fetchVacationBalanceForYear(currentMonthRef.current.getFullYear());
    }

    return success;
  };

  // Optimized month change handler
  const handleMonthChange = useCallback((newMonth) => {
    currentMonthRef.current = newMonth; // Update ref immediately
    setCurrentMonth(newMonth); // Update state for display
  }, []);

  // Load data when component mounts or month changes
  useEffect(() => {
    if (loadWorkStatusesForMonth && teamMembers.length > 0) {
      loadWorkStatusesForMonth(currentMonthRef.current); // Use ref

      // Load holidays for the current month's year
      const year = currentMonthRef.current.getFullYear();
      loadHolidays(year);

    }
  }, [currentMonth, teamMembers.length, loadWorkStatusesForMonth, loadHolidays]);

  // Fetch collective vacations when form is shown
  useEffect(() => {
    if (showCollectiveVacationForm && currentUserRole === 'admin') {
      fetchCollectiveVacations();
    }
  }, [showCollectiveVacationForm, currentUserRole, fetchCollectiveVacations]);

  // Load collective vacations when component mounts (for calendar display) - for all users
  useEffect(() => {
    fetchCollectiveVacations();
  }, [currentUserRole, fetchCollectiveVacations]);

  // Refresh year status when viewing dashboard
  useEffect(() => {
    refetchYearStatus();
  }, [refetchYearStatus]);

  // Auto-open modal when single day is selected
  useEffect(() => {
    if (selectedDay) {
      setShowStatusModal(true);
    }
  }, [selectedDay]);

  // Show loading state
  if (loading) {
    return (
      <div style={{
        padding: '24px',
        maxWidth: '98vw',
        margin: '0 auto',
        width: '100%',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading...</div>
        </div>
      </div>
    );
  }

  const closeLabel = translateWithFallback('close', 'Close');

  return (
    <div style={{
      padding: '24px',
      maxWidth: '98vw',
      margin: '0 auto',
      width: '100%',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh'
    }}>
      <Header
        currentUser={currentUserDisplay}
        vacationBalance={vacationBalance}
        onLogout={onLogout}
        currentLanguage={currentLanguage}
        setCurrentLanguage={setCurrentLanguage}
        onChangePassword={() => setShowPasswordModal(true)}
        showChangePassword={showChangePassword}
        currentMonth={currentMonth}
        fetchVacationBalanceForYear={fetchVacationBalanceForYear}
        vacationBalanceForYear={vacationBalanceForYear}
      />

      {/* Admin Navigation Buttons */}
      {currentUserRole === 'admin' && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '24px',
          alignItems: 'center'
        }}>
          <button
            onClick={() => setCurrentView('admin')}
            style={{
              padding: '10px 20px',
              backgroundColor: currentView === 'admin' ? '#dc2626' : 'transparent',
              color: currentView === 'admin' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {t('userManagement', {}, currentLanguage)}
          </button>
          <button
            onClick={() => setShowCollectiveVacationForm(!showCollectiveVacationForm)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: '1px solid #d97706',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#d97706'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#f59e0b'}
          >
            <Users size={16} />
            {t('collectiveVacation', {}, currentLanguage)}
          </button>

          {/* Admin Edit Mode Toggle */}
          <button
            onClick={() => setAdminEditMode(!adminEditMode)}
            style={{
              padding: '10px 20px',
              backgroundColor: adminEditMode ? '#dc2626' : '#6b7280',
              color: 'white',
              border: '1px solid ' + (adminEditMode ? '#b91c1c' : '#4b5563'),
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title={adminEditMode ?
              (t('disableAdminEdit', {}, currentLanguage)) :
              (t('enableAdminEdit', {}, currentLanguage))
            }
          >
            <Edit size={16} />
            {adminEditMode ?
              (t('adminEditOn', {}, currentLanguage)) :
              (t('adminEditOff', {}, currentLanguage))
            }
          </button>

          {/* Direct Excel Download Button */}
          <button
            onClick={async () => { setShowGenerateReportModal(!showGenerateReportModal) }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#059669',
              color: 'white',
              border: '1px solid #047857',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              marginLeft: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title={t('downloadExcelReport', {}, currentLanguage)}
          >
            <FileSpreadsheet size={16} />
            {t('downloadReport', {}, currentLanguage)}
          </button>
        </div>
      )}

      <GenerateReportModal
        isOpen={showGenerateReportModal}
        onClose={() => setShowGenerateReportModal(false)}
        currentMonth={currentMonth}
        currentLanguage={currentLanguage}
      />

      {/* Collective Vacation Form */}
      <CollectiveVacationForm
        show={showCollectiveVacationForm && currentUserRole === 'admin'}
        setShowCollectiveVacationForm={setShowCollectiveVacationForm}
        collectiveVacationData={collectiveVacationData}
        setCollectiveVacationData={setCollectiveVacationData}
        collectiveVacations={collectiveVacations}
        fetchCollectiveVacations={fetchCollectiveVacations}
        loadWorkStatusesForMonth={loadWorkStatusesForMonth}
        currentMonth={currentMonthRef.current} // Use ref for operations
        showDeleteConfirmation={showDeleteConfirmation}
        currentLanguage={currentLanguage}
      />

      <StatsCards
        stats={stats}
        currentLanguage={currentLanguage}
      />

      <MonthNavigation
        currentMonth={currentMonth}
        setCurrentMonth={handleMonthChange}
        currentLanguage={currentLanguage}
      />

      {/* Calendar */}
      {isMobile ? (
        <MobileAgenda
          month={currentMonth}
          currentUser={currentUserDisplay}
          getStatusForUserAndDate={getStatusForUserAndDate}
          handleCellClick={handleCellClickWithValidation}
          currentLanguage={currentLanguage}
          t={t}
          isCroatianHoliday={isCroatianHoliday}
          getHolidayName={getHolidayName}
          collectiveVacations={collectiveVacations}
          userEmploymentData={userEmploymentData}
        />
      ) : (
      <CalendarTable
        month={currentMonth}
        teamMembers={teamMembers}
        getStatusForUserAndDate={getStatusForUserAndDate}
        currentUser={currentUserDisplay}
        handleCellClick={handleCellClickWithValidation}
        handleMouseDown={handleMouseDown}
        handleMouseEnter={handleMouseEnter}
        handleMouseUp={wrappedHandleMouseUp}
        isDateSelected={isDateSelected}
        currentLanguage={currentLanguage}
        t={t}
        isCroatianHoliday={isCroatianHoliday}
        getHolidayName={getHolidayName}
        collectiveVacations={collectiveVacations}
        currentUserRole={currentUserRole}
        adminEditMode={adminEditMode}
        userEmploymentData={userEmploymentData}
        getFullNameFromDisplay={getFullNameFromDisplay}
      />
      )}

      <StatusModal
        showModal={showStatusModal}
        setShowModal={setShowStatusModal}
        selectedDay={selectedDay}
        selectedRange={selectedRange}
        updateStatus={updateStatusWithRefresh}
        clearSelection={clearSelection}
        currentUser={selectedUser || currentUserDisplay}
        getStatusForUserAndDate={getStatusForUserAndDate}
        t={t}
        adminEditMode={adminEditMode}
        currentUserRole={currentUserRole}
        collectiveVacations={collectiveVacations}
      />

      {/* Delete Collective Vacation Confirmation Modal */}
      <DeleteConfirmModal
        show={showDeleteConfirmModal}
        item={vacationToDelete}
        itemName="collective vacation"
        onClose={() => {
          setShowDeleteConfirmModal(false);
          setVacationToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        currentLanguage={currentLanguage}
      />

      {/* Password Change Modal */}
      <PasswordChangeModal
        show={showPasswordModal}
        passwordData={passwordData}
        setPasswordData={setPasswordData}
        handlePasswordChange={handlePasswordChange}
        onClose={() => setShowPasswordModal(false)}
        t={t}
        currentLanguage={currentLanguage}
      />

      {/* Vacation Warning Modal */}
      <VacationWarningModal
        isOpen={vacationWarningModal.isOpen}
        adminMode={vacationWarningModal.adminMode}
        onClose={() => setVacationWarningModal({ isOpen: false, remaining: 0, requested: 0 })}
        remaining={vacationWarningModal.remaining}
        requested={vacationWarningModal.requested}
        currentLanguage={currentLanguage}
        t={t}
      />

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={handleErrorModalClose}
        title={t('genericError', {}, currentLanguage)}
        type="error"
        actions={
          <>
            <button
              onClick={handleErrorModalClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              {closeLabel}
            </button>
          </>
        }
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
}

export default Dashboard;
